import { PanelItemFieldTypeEnum, UnitsEnum, UnitSystemEnum } from '@/__generated__/graphql';
import { NOT_APPLICABLE_LABEL } from '@/constants';
import { formatPercentage, localiseMeasurement } from '@/utils/unitSystem';
import { formatEnumForDisplay, formatTimeStamps, isTimeStamp, titleCase } from '@/utils';

const DATE_DISPLAY_FORMAT = 'dd/MMM/yyyy';

type JsonPrimitive = string | number | boolean | null;
export type JsonObject = { [key: string]: JsonValue };
export type JsonValue = JsonPrimitive | JsonValue[] | JsonObject;

type FormatContext = {
  raw: JsonValue;
  unitSystem: UnitSystemEnum;
};

type Formatter = (context: FormatContext) => string;

type ValueAndPercentageShape = JsonObject & { value: number; percentage?: number | null };

const isValueAndPercentage = (raw: JsonValue): raw is ValueAndPercentageShape => {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return false;
  return typeof raw.value === 'number';
};

const meterFormatter: Formatter = ({ raw, unitSystem }) =>
  localiseMeasurement({
    unitSystem,
    sourceUnit: UnitsEnum.Meter,
    displayAsUnit: UnitsEnum.Millimeter,
    showUnitLabel: true,
    value: Number(raw),
  });

const meterToMeterFormatter: Formatter = ({ raw, unitSystem }) =>
  localiseMeasurement({
    unitSystem,
    sourceUnit: UnitsEnum.Meter,
    showUnitLabel: true,
    value: Number(raw),
  });

const valueAndPercentageFormatter: Formatter = ({ raw, unitSystem }) => {
  if (!isValueAndPercentage(raw)) return NOT_APPLICABLE_LABEL;
  const formattedValue = localiseMeasurement({
    unitSystem,
    sourceUnit: UnitsEnum.Meter,
    displayAsUnit: UnitsEnum.Millimeter,
    showUnitLabel: true,
    value: raw.value,
  });
  return `${formattedValue}${formatPercentage(raw.percentage ?? null)}`;
};

const titleCaseFormatter: Formatter = ({ raw }) => titleCase(String(raw)) || NOT_APPLICABLE_LABEL;

const enumFormatter: Formatter = ({ raw }) =>
  formatEnumForDisplay(String(raw)) || NOT_APPLICABLE_LABEL;

const dateFormatter: Formatter = ({ raw }) => {
  if (raw === null || raw === undefined) return NOT_APPLICABLE_LABEL;
  const candidate = typeof raw === 'number' ? raw : String(raw);
  if (!isTimeStamp(candidate)) return NOT_APPLICABLE_LABEL;
  return formatTimeStamps(candidate, DATE_DISPLAY_FORMAT);
};

const primitiveFormatter: Formatter = ({ raw }) => {
  if (typeof raw === 'object') return JSON.stringify(raw);
  return String(raw);
};

type FormatKind =
  | 'meterSimple'
  | 'meter'
  | 'valueAndPercentage'
  | 'titleCase'
  | 'enum'
  | 'date'
  | 'primitive';

const FORMATTERS: Record<FormatKind, Formatter> = {
  meterSimple: meterToMeterFormatter,
  meter: meterFormatter,
  valueAndPercentage: valueAndPercentageFormatter,
  titleCase: titleCaseFormatter,
  enum: enumFormatter,
  date: dateFormatter,
  primitive: primitiveFormatter,
};

const FIELD_FORMAT_OVERRIDES: Partial<Record<PanelItemFieldTypeEnum, Record<string, FormatKind>>> =
  {
    [PanelItemFieldTypeEnum.Blister]: {
      maxHeight: 'meter',
      estimatedHeight: 'meter',
      pdrHeight: 'meter',
      manualSetHeight: 'meter',
      verifiedMaxHeight: 'meter',
      verifiedResidualThickness: 'meter',
      wallLoss: 'valueAndPercentage',
      residualCorrosionAllowance: 'valueAndPercentage',
      residualThickness: 'valueAndPercentage',
    },
    [PanelItemFieldTypeEnum.PointOfInterest]: {
      deckRelativeHeight: 'meterSimple',
      viewpointRelativeHeight: 'meterSimple',
      visibility: 'titleCase',
      accessibility: 'enum',
      dueDate: 'date',
      createdAt: 'date',
      updatedAt: 'date',
      remediationAt: 'date',
      lastModifiedByAPI: 'date',
      deletedAt: 'date',
    },
  };

export type FormatPanelValueParameters = {
  fieldType: PanelItemFieldTypeEnum;
  key: string;
  raw: JsonValue | undefined;
  unitSystem: UnitSystemEnum;
};

export const formatPanelValue = ({
  fieldType,
  key,
  raw,
  unitSystem,
}: FormatPanelValueParameters): string => {
  if (raw === null || raw === undefined) return NOT_APPLICABLE_LABEL;

  const overrideKind = FIELD_FORMAT_OVERRIDES[fieldType]?.[key];
  if (overrideKind) return FORMATTERS[overrideKind]({ raw, unitSystem });

  if (isValueAndPercentage(raw)) return FORMATTERS.valueAndPercentage({ raw, unitSystem });

  return FORMATTERS.primitive({ raw, unitSystem });
};
